import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getTeamService, type InviteTeamMemberPayload, type TeamMember, type ListFilters, type ResultList } from '@arcual/lib-app-platform';
import { handleHttpError } from '@/utils/errorHandlers';

const TeamStoreName = 'TeamStore';

export const useTeamStore = defineStore(TeamStoreName, () => {
  const teamMembers = ref<TeamMember[]>([]);

  const getTeamMembers = async (filters?: ListFilters): Promise<ResultList<TeamMember>> => {
    try {

      const result = await getTeamService().getTeamMembers(filters);

      teamMembers.value = result.items;
      return result;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const inviteTeamMember = async (payload: InviteTeamMemberPayload): Promise<TeamMember> => {
    try {
      const newMember = await getTeamService().inviteTeamMember(payload);
      teamMembers.value.push(newMember);
      return newMember;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const removeTeamMember = async (memberId: string): Promise<void> => {
    try {
      await getTeamService().removeTeamMember(memberId);
      teamMembers.value = teamMembers.value.filter((member) => member.id !== memberId);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const updateTeamMember = async (memberId: string, payload: Partial<TeamMember>): Promise<TeamMember> => {
    try {
      const updatedMember = await getTeamService().updateTeamMember(memberId, payload);
      const index = teamMembers.value.findIndex((member) => member.id === memberId);
      if (index !== -1) {
        teamMembers.value[index] = updatedMember;
      }
      return updatedMember;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const enableTeamMember = async (memberId: string): Promise<void> => {
    try {
      await getTeamService().enableTeamMember(memberId);

      await getTeamMembers();
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const disableTeamMember = async (memberId: string): Promise<void> => {
    try {
      await getTeamService().disableTeamMember(memberId);

      await getTeamMembers();
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  /**
   * Reset the store to its initial state
   */
  const reset = () => {
    teamMembers.value = [];
  };

  return {
    teamMembers,
    getTeamMembers,
    inviteTeamMember,
    removeTeamMember,
    updateTeamMember,
    enableTeamMember,
    disableTeamMember,
    reset,
  };
});
